import { API_URL } from '../config';
import { Family } from '../types';

interface FamiliesResponse {
  families: Family[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

export const familyService = {
  // Obtener todas las familias sin paginación (para compatibilidad)
  async getFamilies(categoryId?: string): Promise<Family[]> {
    // Especificar un límite alto para obtener todas las familias
    const url = categoryId 
      ? `${API_URL}/families?category_id=${categoryId}&limit=100` 
      : `${API_URL}/families?limit=100`;
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (!response.ok) {
      throw new Error('Error al obtener familias');
    }

    const data = await response.json();
    // Si la respuesta tiene el formato nuevo con paginación, devolver solo las familias
    return data.families || data;
  },

  // Obtener familias con paginación
  async getFamiliesWithPagination(page: number = 1, limit: number = 5, categoryId?: string, searchTerm?: string): Promise<FamiliesResponse> {
    let url = `${API_URL}/families?page=${page}&limit=${limit}`;
    if (categoryId) {
      url += `&category_id=${categoryId}`;
    }
    if (searchTerm && searchTerm.trim()) {
      url += `&search=${encodeURIComponent(searchTerm.trim())}`;
    }
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error en la respuesta:', errorText);
      throw new Error(`Error al obtener familias: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    // Validar que la respuesta tenga la estructura esperada
    if (!data || !data.families || !data.pagination) {
      console.error('Respuesta del servidor con estructura incorrecta:', data);
      throw new Error('Estructura de respuesta inválida del servidor');
    }
    
    return data;
  },

  // Obtener familia por ID
  async getFamilyById(id: string): Promise<Family> {
    const response = await fetch(`${API_URL}/families/${id}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (!response.ok) {
      throw new Error('Error al obtener familia');
    }

    return response.json();
  },

  // Crear nueva familia
  async createFamily(familyData: FormData): Promise<Family> {
    const response = await fetch(`${API_URL}/families`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: familyData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Error al crear familia');
    }

    return response.json();
  },

  // Actualizar familia
  async updateFamily(id: string, familyData: FormData): Promise<Family> {
    const response = await fetch(`${API_URL}/families/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: familyData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Error al actualizar familia');
    }

    return response.json();
  },

  // Eliminar familia
  async deleteFamily(id: string): Promise<void> {
    const response = await fetch(`${API_URL}/families/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Error al eliminar familia');
    }
  }
};